import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { passwordProblem, MIN_PASSWORD_LENGTH } from '../utils/passwordRules';

const REDIRECT_AFTER_MS = 4000;

/**
 * Choose a new password: /reset-password?token=...
 *
 * The token is taken out of the address bar as soon as it has been read, the
 * same as VerifyEmail does, and for a sharper reason: this token IS the
 * account until it expires or is spent. Left in the URL it would sit in
 * browser history, in any Referer sent by an image or a font on this page,
 * and in the tab title a screen-share would show.
 *
 * Nothing is submitted on load. Unlike verification -- which the person has
 * already consented to by clicking -- a reset needs a password typed, so the
 * mail-scanner problem that shapes VerifyEmail does not arise here: a scanner
 * fetching this link renders a form and goes away.
 *
 * VALIDATION IS MIRRORED, NOT MOVED. utils/passwordRules.js holds the same
 * rules the server applies and returns the same error CODES, so the message
 * a person sees is identical whether it was caught here or there. The server
 * remains the authority -- it checks the rules this page cannot, such as
 * whether the password contains the account's own name, which this page does
 * not know because it never learns whose token this is.
 */
export default function ResetPassword() {
  const { t, i18n } = useTranslation(['passwordReset', 'register']);
  const navigate = useNavigate();
  const { search } = useLocation();
  const tokenRef = useRef(new URLSearchParams(search).get('token') || '');
  const token = tokenRef.current;

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [state, setState] = useState(token ? 'idle' : 'invalid');
  const [errorCode, setErrorCode] = useState(null);

  useEffect(() => {
    if (!token) return;
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch {
      // Not fatal; the token is single-use and short-lived either way.
    }
  }, [token]);

  useEffect(() => {
    if (state !== 'done') return undefined;
    const id = setTimeout(() => navigate('/login', { replace: true }), REDIRECT_AFTER_MS);
    return () => clearTimeout(id);
  }, [state, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    setErrorCode(null);

    // Checked before the round trip, in this order: a mismatch is the most
    // common mistake and saying "too short" about a typo in the second box
    // would send somebody looking in the wrong place.
    if (password !== confirm) {
      setErrorCode('passwordMismatch');
      return;
    }
    const problem = passwordProblem(password);
    if (problem) {
      setErrorCode(problem);
      return;
    }

    setState('sending');
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setState('done');
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      if (status === 410) setState('expired');
      else if (status === 400 && code === 'invalid_token') setState('invalid');
      else {
        // A 400 with a password code is a rule this page could not check.
        setState('idle');
        setErrorCode(code || 'generic');
      }
    }
  };

  const problemText = errorCode
    ? t([`register:errors.${errorCode}`, 'register:errors.generic'])
    : null;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.langRow}>
          <button type="button" style={styles.langBtn} title={t('tip.language')}
                  onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'he' : 'en')}>
            {i18n.language === 'en' ? 'עב' : 'EN'}
          </button>
        </div>
        <h1 style={styles.title}>{t('reset.title')}</h1>

        {state === 'done' && (
          <div role="status" data-testid="reset-done">
            <p style={styles.success}>{t('reset.done')}</p>
            <p style={styles.text}>{t('reset.doneSignedOut')}</p>
            <Link to="/login" title={t('tip.backToSignIn')} style={styles.link}>{t('reset.goToSignIn')}</Link>
          </div>
        )}

        {state === 'expired' && (
          <div data-testid="reset-expired">
            <p role="alert" style={styles.problem}>{t('reset.expired')}</p>
            <Link to="/forgot-password" title={t('tip.askAgain')} style={styles.link}>{t('reset.askAgain')}</Link>
          </div>
        )}

        {state === 'invalid' && (
          <div data-testid="reset-invalid">
            <p role="alert" style={styles.problem}>{t('reset.invalid')}</p>
            <Link to="/forgot-password" title={t('tip.askAgain')} style={styles.link}>{t('reset.askAgain')}</Link>
          </div>
        )}

        {(state === 'idle' || state === 'sending') && (
          <form onSubmit={submit}>
            <p style={styles.text}>{t('reset.help', { min: MIN_PASSWORD_LENGTH })}</p>

            <label style={styles.label} htmlFor="reset-password">{t('reset.newPassword')}</label>
            <input
              id="reset-password"
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              dir="ltr"
              required
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErrorCode(null); }}
              style={styles.input}
              title={t('tip.newPassword')}
              data-testid="reset-password"
            />

            <label style={styles.label} htmlFor="reset-confirm">{t('reset.confirmPassword')}</label>
            <input
              id="reset-confirm"
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              dir="ltr"
              required
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setErrorCode(null); }}
              style={styles.input}
              title={t('tip.confirmPassword')}
              data-testid="reset-confirm"
            />

            <label style={styles.showRow}>
              <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} title={t('tip.showPassword')} />
              {t('reset.showPassword')}
            </label>

            {problemText && (
              <p role="alert" style={styles.problem} data-testid="reset-error">{problemText}</p>
            )}

            <button type="submit" disabled={state === 'sending'}
                    style={{ ...styles.primary, opacity: state === 'sending' ? 0.5 : 1 }}
                    title={t('tip.resetSubmit')}
                    data-testid="reset-submit">
              {state === 'sending' ? t('reset.saving') : t('reset.submit')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(160deg, var(--c-primary-soft-3) 0%, var(--c-primary-soft-4) 50%, var(--c-primary-soft-5) 100%)', padding: 16,
  },
  card: {
    width: '100%', maxWidth: 440, background: 'var(--c-surface)', borderRadius: 16, padding: '28px 32px',
    boxShadow: '0 8px 40px rgba(44, 110, 73, 0.12), 0 2px 8px rgba(0,0,0,0.06)',
  },
  langRow: { display: 'flex', justifyContent: 'flex-end' },
  langBtn: {
    border: '1px solid var(--c-border-strong)', borderRadius: 6, background: 'var(--c-surface)',
    padding: '4px 10px', cursor: 'pointer', fontSize: 12,
  },
  title: { fontSize: 20, color: 'var(--c-primary-fg)', margin: '8px 0 16px' },
  text: { fontSize: 14, color: 'var(--c-text-body-fg)', lineHeight: 1.5 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, margin: '14px 0 4px' },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 16,
    border: '1px solid var(--c-border-strong)', borderRadius: 8, background: 'var(--c-surface)',
  },
  showRow: {
    display: 'flex', alignItems: 'center', gap: 6, marginTop: 10,
    fontSize: 13, color: 'var(--c-text-muted-fg)',
  },
  primary: {
    width: '100%', marginTop: 16, padding: '11px 16px', border: 'none', borderRadius: 8,
    background: 'var(--c-primary)', color: 'var(--c-on-accent)', fontSize: 15, fontWeight: 600,
    cursor: 'pointer',
  },
  success: { fontSize: 15, fontWeight: 600, color: 'var(--c-success-3-fg)' },
  problem: { fontSize: 14, fontWeight: 600, color: 'var(--c-danger-2-fg)', marginTop: 10 },
  link: { color: 'var(--c-primary-fg)', fontWeight: 600 },
};
